import { useEffect, useState } from "react";
import { App, Card, Checkbox, Space, Typography } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { updateStageData } from "../../lib/api";
import type { WorkflowStage } from "../intakeDetails/types";

interface Props {
  workflowId: string;
  stage: WorkflowStage;
  /** Called after a successful save so the parent can refetch and re-evaluate
   *  the Advance button's enabled state. */
  onSaved?: () => void;
}

/**
 * Leadership "brief sent" confirmation — single checkbox bound to
 * `workflow.stages[leadership_presentation].briefSentToLeadership`. Saves
 * immediately on toggle. Advancing past this stage is gated on the box being
 * checked (see `WorkflowTab.canAdvanceFromHere` + backend gating).
 */
export default function LeadershipBriefSent({ workflowId, stage, onSaved }: Props) {
  const { message } = App.useApp();
  const [checked, setChecked] = useState<boolean>(Boolean(stage.briefSentToLeadership));
  const [saving, setSaving] = useState(false);

  // Re-sync from props when the parent refetches the workflow bundle.
  useEffect(() => {
    setChecked(Boolean(stage.briefSentToLeadership));
  }, [stage.briefSentToLeadership]);

  const handleChange = async (e: CheckboxChangeEvent) => {
    const next = e.target.checked;
    const previous = checked;
    setChecked(next);
    setSaving(true);
    try {
      await updateStageData(workflowId, stage.id, { briefSentToLeadership: next });
      onSaved?.();
    } catch (error) {
      // Roll back optimistic state so users see the actual persisted value.
      setChecked(previous);
      if (error instanceof Error) message.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card size="small" title="Leadership brief delivery">
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Typography.Text type="secondary">
          Confirm the leadership brief was sent before advancing to the
          Approval Decision stage.
        </Typography.Text>
        <Checkbox
          checked={checked}
          disabled={saving}
          onChange={handleChange}
          aria-label="Brief has been sent to leadership"
        >
          Brief has been sent to leadership
        </Checkbox>
      </Space>
    </Card>
  );
}
